import { useAuthStore, useErrorStore, useLoadingStore } from '../state/store'
import { AmplitudeEvents, useAmplitudeTrack } from './amplitude'
import { httpClient } from '../utils/http'
import { AxiosError } from 'axios'

const NO_DATA = 'N/A'

const formatDateToYYYYMMDD = (
    dateString: string | null | undefined
): string => {
    if (!dateString) return NO_DATA
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return NO_DATA
        return date.toISOString().split('T')[0]
    } catch {
        return NO_DATA
    }
}

const convertToYahooFormat = (ticker: string) => {
    if (!ticker || ticker.trim().toUpperCase() === 'N/A') {
        return ticker.trim()
    }
    const tickerTrimmed = ticker.trim()
    const match = tickerTrimmed.match(/^(.+?)(\.[A-Z]{1,4})$/)
    if (match) {
        const base = match[1]
        const suffix = match[2]
        const baseConverted = base.replace(/\./g, '-')
        return baseConverted + suffix
    } else {
        if ((tickerTrimmed.match(/\./g) || []).length === 1) {
            return tickerTrimmed.replace('.', '-')
        }
        return tickerTrimmed
    }
}

export const useBrokerActions = () => {
    const { setLoading } = useLoadingStore((state) => state)
    const { setError } = useErrorStore((state) => state)
    const { currentUser } = useAuthStore((state) => state)
    const trackEvent = useAmplitudeTrack()

    const refreshSnapTrade = async () => {
        if (!currentUser.isSubscribed) {
            setError({
                hasError: true,
                errorMessage:
                    'Upgrade to our Pro plan for access. <a href="https://www.wisesheets.io/payment?plan=PRO">Upgrade.</a>',
            })
            return
        }

        setLoading({ isLoading: true, message: 'Syncing...' })
        trackEvent(AmplitudeEvents.REFRESH_BROKER)

        try {
            const response = await httpClient.post(
                '/api/private/snaptrade/holdings'
            )
            const holdings = response.data

            const dataToWrite = [
                [
                    'Account',
                    'Symbol',
                    'Options Contract',
                    'Expiry',
                    'Company name',
                    'Exchange',
                    'Currency',
                    'Asset type',
                    'Units held',
                    'Average purchase price',
                    'Current price',
                    'Total cost',
                    'Market value',
                    'Unrealized P&L',
                    'Unrealized P&L (%)',
                ],
            ]

            for (let i = 0; i < holdings.length; i++) {
                const holding = holdings[i]
                let row = []
                const positions = holding.positions

                if (positions && positions.length > 0) {
                    for (let j = 0; j < positions.length; j++) {
                        const position = positions[j]
                        if (!position) continue

                        const units = position.units || 0
                        const avgPrice = position.average_purchase_price || 0
                        const currentPrice = position.price || 0
                        const totalCost = units * avgPrice
                        const marketValue = units * currentPrice
                        const unrealisedPL = marketValue - totalCost
                        const unrealisedPLPercentage =
                            totalCost !== 0 ? unrealisedPL / totalCost : 0

                        row.push(
                            holding.account_name ||
                                holding.account?.name ||
                                NO_DATA
                        )
                        row.push(
                            convertToYahooFormat(
                                position.symbol?.symbol?.symbol || NO_DATA
                            )
                        )
                        row.push(NO_DATA)
                        row.push(NO_DATA)
                        row.push(
                            position.symbol?.symbol?.description || NO_DATA
                        )
                        row.push(
                            position.symbol?.symbol?.exchange?.code || NO_DATA
                        )
                        row.push(position.currency?.code || NO_DATA)
                        row.push(
                            position.symbol?.symbol?.type?.description ||
                                NO_DATA
                        )
                        row.push(units)
                        row.push(avgPrice)
                        row.push(currentPrice)
                        row.push(totalCost)
                        row.push(marketValue)
                        row.push(unrealisedPL)
                        row.push(unrealisedPLPercentage)

                        dataToWrite.push(row)
                        row = []
                    }
                }

                const optionPositions = holding.option_positions

                if (optionPositions && optionPositions.length > 0) {
                    for (let j = 0; j < optionPositions.length; j++) {
                        const position = optionPositions[j]
                        if (!position) continue

                        const units = position.units || 0
                        const avgPrice = position.average_purchase_price || 0
                        const currentPrice = position.price || 0
                        const totalCost = units * avgPrice
                        const marketValue = units * currentPrice
                        const unrealisedPL = marketValue - totalCost
                        const unrealisedPLPercentage =
                            totalCost !== 0 ? unrealisedPL / totalCost : 0

                        const optionTicker =
                            position.symbol?.option_symbol?.ticker
                        const tickerParts = optionTicker
                            ? optionTicker.split(' ')
                            : []
                        const optionContract =
                            tickerParts.length > 1
                                ? tickerParts[tickerParts.length - 1]
                                : NO_DATA

                        row.push(
                            holding.account_name ||
                                holding.account?.name ||
                                NO_DATA
                        )
                        row.push(
                            convertToYahooFormat(
                                position.symbol?.option_symbol
                                    ?.underlying_symbol?.symbol || NO_DATA
                            )
                        )
                        row.push(optionContract)
                        row.push(
                            position.symbol?.option_symbol?.expiration_date ||
                                NO_DATA
                        )
                        row.push(
                            position.symbol?.option_symbol?.underlying_symbol
                                ?.description || NO_DATA
                        )
                        row.push(
                            position.symbol?.option_symbol?.underlying_symbol
                                ?.exchange?.code || NO_DATA
                        )
                        row.push(position.currency?.code || NO_DATA)
                        row.push(
                            position.symbol?.option_symbol?.underlying_symbol
                                ?.description || NO_DATA
                        )
                        row.push(units)
                        row.push(avgPrice)
                        row.push(currentPrice)
                        row.push(totalCost)
                        row.push(marketValue)
                        row.push(unrealisedPL)
                        row.push(unrealisedPLPercentage)

                        dataToWrite.push(row)
                        row = []
                    }
                }
            }

            if (dataToWrite.length === 0) {
                setError({
                    hasError: true,
                    errorMessage:
                        'No broker data found. Please connect your broker.',
                })
                return
            }

            await Excel.run(async (context) => {
                const currentSheet =
                    context.workbook.worksheets.getActiveWorksheet()
                const dataToWriteRange = currentSheet
                    .getRange('A1')
                    .getResizedRange(
                        dataToWrite.length - 1,
                        dataToWrite[0].length - 1
                    )
                dataToWriteRange.values = dataToWrite

                const accountRow = currentSheet
                    .getRange('A1')
                    .getResizedRange(0, dataToWrite[0].length - 1)
                accountRow.format.fill.color = '#364165'
                accountRow.format.font.color = '#F7F8F8'
                accountRow.format.font.bold = true

                currentSheet.autoFilter.apply(dataToWriteRange)
                dataToWriteRange.format.autofitColumns()

                const purhcasePrice = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`J2:J${dataToWrite[0].length + 1}`)
                purhcasePrice.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const price = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`K2:K${dataToWrite[0].length + 1}`)
                price.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const totalCost = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`L2:L${dataToWrite[0].length + 1}`)
                totalCost.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const marketValue = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`M2:M${dataToWrite[0].length + 1}`)
                marketValue.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const unrealisedPL = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`N2:N${dataToWrite[0].length + 1}`)
                unrealisedPL.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const unrealisedPLPercentage = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`O2:O${dataToWrite[0].length + 1}`)
                unrealisedPLPercentage.numberFormat = [['0.00%_);(0.00%)']]

                await context.sync()
            })
        } catch (error) {
            if (error instanceof AxiosError) {
                const errorMsg =
                    typeof error.response?.data === 'string'
                        ? error.response.data
                        : 'Something unexpected happened. Please let us know and we will fix it.'
                setError({ hasError: true, errorMessage: errorMsg })
            } else {
                setError({
                    hasError: true,
                    errorMessage: 'Something went wrong. Please try again.',
                })
            }
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    const refreshSnapTradeTransactions = async () => {
        if (!currentUser.isSubscribed) {
            setError({
                hasError: true,
                errorMessage:
                    'Upgrade to our Pro plan for access. <a href="https://www.wisesheets.io/payment?plan=PRO">Upgrade.</a>',
            })
            return
        }
        setLoading({ isLoading: true, message: 'Syncing...' })
        trackEvent(AmplitudeEvents.REFRESH_TRANSACTIONS)

        try {
            const response = await httpClient.get(
                '/api/private/snaptrade/transactions'
            )
            const transactions = response.data

            const dataToWrite = [
                [
                    'Account',
                    'Trade Date',
                    'Settlement Date',
                    'Status',
                    'Type',
                    'Option Type',
                    'Symbol',
                    'Description',
                    'Option Ticker',
                    'Underlying Symbol',
                    'Strike Price',
                    'Expiration Date',
                    'Price',
                    'Fee',
                    'Currency',
                    'FX Rate',
                    'Amount',
                    'Institution',
                    'Profit/Loss',
                ],
            ]

            for (let i = 0; i < transactions.length; i++) {
                const transaction = transactions[i]
                const settlementDate = transaction.settlement_date
                    ? new Date(transaction.settlement_date)
                    : null
                const currentDate = new Date()
                const status =
                    settlementDate && settlementDate < currentDate
                        ? 'Settled'
                        : 'Pending'

                const marketValue =
                    (transaction.price || 0) * (transaction.units || 0)
                const profitLoss = marketValue - (transaction.amount || 0)

                dataToWrite.push([
                    transaction.account_name || NO_DATA,
                    formatDateToYYYYMMDD(transaction.trade_date),
                    formatDateToYYYYMMDD(transaction.settlement_date),
                    status,
                    transaction.type || NO_DATA,
                    transaction.option_type || NO_DATA,
                    transaction.symbol?.symbol || NO_DATA,
                    transaction.description || NO_DATA,
                    transaction.option_symbol?.ticker || NO_DATA,
                    transaction.option_symbol?.underlying_symbol?.symbol ||
                        NO_DATA,
                    transaction.option_symbol?.strike_price || NO_DATA,
                    formatDateToYYYYMMDD(
                        transaction.option_symbol?.expiration_date
                    ),
                    transaction.price || 0,
                    transaction.fee || 0,
                    transaction.currency?.code || NO_DATA,
                    transaction.fx_rate || NO_DATA,
                    transaction.amount || 0,
                    transaction.institution || NO_DATA,
                    profitLoss,
                ])
            }

            if (dataToWrite.length === 1) {
                setError({
                    hasError: true,
                    errorMessage: 'No transaction data found.',
                })
                return
            }

            await Excel.run(async (context) => {
                const currentSheet =
                    context.workbook.worksheets.getActiveWorksheet()
                const dataToWriteRange = currentSheet
                    .getRange('A1')
                    .getResizedRange(
                        dataToWrite.length - 1,
                        dataToWrite[0].length - 1
                    )
                dataToWriteRange.values = dataToWrite

                const headerRow = currentSheet
                    .getRange('A1')
                    .getResizedRange(0, dataToWrite[0].length - 1)
                headerRow.format.fill.color = '#364165'
                headerRow.format.font.color = '#F7F8F8'
                headerRow.format.font.bold = true

                currentSheet.autoFilter.apply(dataToWriteRange)
                dataToWriteRange.format.autofitColumns()

                const priceColumn = currentSheet.getRange(
                    `M2:M${dataToWrite.length}`
                )
                priceColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const feeColumn = currentSheet.getRange(
                    `N2:N${dataToWrite.length}`
                )
                feeColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const amountColumn = currentSheet.getRange(
                    `Q2:Q${dataToWrite.length}`
                )
                amountColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const profitLossColumn = currentSheet.getRange(
                    `S2:S${dataToWrite.length}`
                )
                profitLossColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                await context.sync()
            })
        } catch (error) {
            if (error instanceof AxiosError) {
                const errorMsg =
                    typeof error.response?.data === 'string'
                        ? error.response.data
                        : 'Something unexpected happened. Please let us know and we will fix it.'
                setError({ hasError: true, errorMessage: errorMsg })
            } else {
                setError({
                    hasError: true,
                    errorMessage: 'Something went wrong. Please try again.',
                })
            }
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    return { refreshSnapTrade, refreshSnapTradeTransactions }
}
